/**
 * Per-section review of the DMP.
 *
 * "Approve & Issue" stays disabled until every section is 'reviewed', and the
 * server refuses to issue otherwise (functions/src/versioning/dmpReviewGate.ts,
 * compared with this file on the same inputs in dmpSectionReview.test.ts).
 * These are the rules for the control that sets that status.
 */

export const MARK_REVIEWED_LABEL = 'Mark reviewed';
export const MARK_NOT_REVIEWED_LABEL = 'Mark not reviewed';

/** Fields a review adds to the section document. A text edit clears them all. */
export const REVIEW_FIELDS = ['reviewedBy', 'reviewedByName', 'reviewedByEmail', 'reviewedAt'] as const;

const FAILED = new Set(['generation_failed', 'failed']);

const textOf = (html: string) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function countPlaceholders(html: string): number {
  return (String(html ?? '').match(/\[PLACEHOLDER:[^\]]+\]/g) || []).length;
}

export interface ReviewControlState {
  /** 'mark' offers "Mark reviewed"; 'unmark' offers "Mark not reviewed". */
  mode: 'mark' | 'unmark';
  label: string;
  enabled: boolean;
  /** Why the control is disabled (its tooltip), else null. */
  reason: string | null;
  /** What the reviewer should know before deciding, else null. */
  note: string | null;
}

export function reviewControlState(args: {
  status?: string | null;
  content?: string | null;
  editLocked?: boolean;
  stale?: boolean;
}): ReviewControlState {
  const status = String(args.status ?? '');
  const reviewed = status === 'reviewed';
  const mode = reviewed ? 'unmark' : 'mark';
  const label = reviewed ? MARK_NOT_REVIEWED_LABEL : MARK_REVIEWED_LABEL;
  const placeholders = reviewed ? 0 : countPlaceholders(args.content ?? '');
  const note = placeholders > 0 ? `${placeholders} placeholder${placeholders === 1 ? '' : 's'} still to fill` : null;

  let reason: string | null = null;
  if (args.editLocked) {
    reason = 'This document is issued. Start a revision to change its review.';
  } else if (args.stale) {
    reason = 'This section was changed elsewhere. Reload the page before reviewing it.';
  } else if (!reviewed) {
    if (FAILED.has(status)) {
      reason = 'This section could not be written automatically. Regenerate it, or write it, before it can be reviewed.';
    } else if (status !== 'generated' && status !== 'edited') {
      reason = 'Only a generated or edited section can be reviewed.';
    } else if (textOf(args.content ?? '') === '') {
      reason = 'This section has no text to review.';
    }
  }
  return { mode, label, enabled: reason === null, reason, note };
}

/** "Mark not reviewed" returns to edited if the user edited the text, else generated. */
export function statusAfterUnreview(section: { userEdited?: boolean }): 'edited' | 'generated' {
  return section.userEdited ? 'edited' : 'generated';
}

/** What marking a section reviewed stores. `at` is a Firestore Timestamp. */
export function reviewRecord(
  user: { uid: string; displayName?: string | null; email?: string | null },
  at: unknown,
): Record<string, unknown> {
  const rec: Record<string, unknown> = { status: 'reviewed', reviewedBy: user.uid };
  if (user.displayName) rec.reviewedByName = user.displayName;
  if (user.email) rec.reviewedByEmail = user.email;
  rec.reviewedAt = at;
  return rec;
}

/** Every review field mapped to the delete marker (Firestore's deleteField). */
export function clearReviewFields<T>(marker: () => T): Record<(typeof REVIEW_FIELDS)[number], T> {
  const out = {} as Record<(typeof REVIEW_FIELDS)[number], T>;
  for (const f of REVIEW_FIELDS) out[f] = marker();
  return out;
}

const dateOf = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const t = v as { toDate?: () => Date };
  return typeof t.toDate === 'function' ? t.toDate() : null;
};

/** "Reviewed by <name or email> on <date>". */
export function reviewedByLine(s: { reviewedByName?: string | null; reviewedByEmail?: string | null; reviewedAt?: unknown }): string {
  const who = s.reviewedByName || s.reviewedByEmail || '';
  const when = dateOf(s.reviewedAt);
  const date = when ? when.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
  if (who && date) return `Reviewed by ${who} on ${date}`;
  if (who) return `Reviewed by ${who}`;
  if (date) return `Reviewed on ${date}`;
  return 'Reviewed';
}

/** The reason "Approve & Issue" is blocked, or null. Same text as the server's. */
export function dmpIssueBlockedReason(sections: ReadonlyArray<{ status?: string | null }> | null | undefined): string | null {
  const list = Array.isArray(sections) ? sections : [];
  if (list.length === 0) return 'The DMP has no sections yet, so there is nothing to review or issue.';
  const unreviewed = list.filter((s) => s?.status !== 'reviewed').length;
  if (unreviewed === 0) return null;
  return `${unreviewed} section${unreviewed === 1 ? '' : 's'} still ${unreviewed === 1 ? 'needs' : 'need'} review`;
}

/** The status list with one section's entry patched; the others are untouched. */
export function patchSectionEntry<T extends { id?: string }>(sections: T[], sectionId: string, patch: Partial<T> & Record<string, unknown>): T[] {
  return sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s));
}

/**
 * The fields a text save writes to the section document. Built here so the
 * rule "an edit ends the review" is one tested function, not a literal in the
 * panel: the new status, and every reviewer field deleted.
 */
export function textSaveFields(args: {
  html: string;
  userEdited: boolean;
  placeholders: string[];
  status: string;
  docVersion: number;
  now: unknown;
  deleteMarker: () => unknown;
}): Record<string, unknown> {
  return {
    content: args.html,
    userEdited: args.userEdited,
    placeholders: args.placeholders,
    status: args.status,
    ...clearReviewFields(args.deleteMarker),
    lastEditedAt: args.now,
    docVersion: args.docVersion,
  };
}
